import { CheckCircle2, Clock, Calendar, HandHeart, AlertCircle, AlertTriangle, Info } from "lucide-react";
import { getDaysSince, formatSmartDate } from "./date";
import { formatDateRange } from "./date-utils";
import { VISIT_TYPES } from "./constants";

/* ============================= */
/* PRIORITY SYSTEM               */
/* ============================= */

export const FEED_PRIORITY = {
  critical: 0,
  warning:  1,
  normal:   2,
  positive: 3,
};

/* ============================= */
/* SEMANTIC STYLE MAPS           */
/* Static only — no interpolation */
/* ============================= */

export const VISIT_DONE_STYLE = {
  icon:               CheckCircle2,
  iconContainerClass: "bg-emerald-50 dark:bg-emerald-950/30",
  iconClass:          "text-emerald-600 dark:text-emerald-400",
};

export const VISIT_PENDING_STYLE = {
  icon:               Clock,
  iconContainerClass: "bg-slate-100 dark:bg-slate-700/40",
  iconClass:          "text-slate-400 dark:text-slate-500",
};

export const EVENT_UPCOMING_STYLE = {
  icon:               Calendar,
  iconContainerClass: "bg-violet-50 dark:bg-violet-950/30",
  iconClass:          "text-violet-600 dark:text-violet-400",
};

export const PRAYER_ACTIVE_STYLE = {
  icon:               HandHeart,
  iconContainerClass: "bg-blue-50 dark:bg-blue-950/30",
  iconClass:          "text-blue-600 dark:text-blue-400",
};

export const PRAYER_URGENT_STYLE = {
  icon:               HandHeart,
  iconContainerClass: "bg-red-50 dark:bg-red-950/30",
  iconClass:          "text-red-600 dark:text-red-400",
};

export const PRIORITY_CRITICAL_STYLE = {
  icon:               AlertCircle,
  iconContainerClass: "bg-red-50 dark:bg-red-950/30",
  iconClass:          "text-red-600 dark:text-red-400",
};

export const PRIORITY_HIGH_STYLE = {
  icon:               AlertTriangle,
  iconContainerClass: "bg-orange-50 dark:bg-orange-950/30",
  iconClass:          "text-orange-600 dark:text-orange-400",
};

export const PRIORITY_MEDIUM_STYLE = {
  icon:               Info,
  iconContainerClass: "bg-amber-50 dark:bg-amber-950/30",
  iconClass:          "text-amber-600 dark:text-amber-400",
};

/* ============================= */
/* VISIT TYPE LOOKUP             */
/* ============================= */

const VISIT_TYPE_MAP = VISIT_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

/* ============================= */
/* NORMALIZER                    */
/* ============================= */

export function normalizeFeedItem(item) {
  return {
    id:                 item.id,
    type:               item.type               || "unknown",
    icon:               item.icon,
    iconClass:          item.iconClass,
    iconContainerClass: item.iconContainerClass,
    title:              item.title              || "—",
    subtitle:           item.subtitle,
    age:                item.age,
    priority:           item.priority           ?? FEED_PRIORITY.normal,
    _date:              item._date              || null,
    onClick:            item.onClick,
    // Extended contract — optional, for future assistant/notifications
    entityType:         item.entityType,
    entityId:           item.entityId,
    route:              item.route,
    meta:               item.meta,
  };
}

/* ============================= */
/* BUILD VISIT FEED              */
/* ============================= */

/**
 * useTypeLabel — true: title = visit type label (PersonDetail)
 *                false: title = contact name (FamilyDetail, ChurchDetail)
 */
export function buildVisitFeed(visits, { nav, useTypeLabel = false } = {}) {
  return visits.map((v) => {
    const isDone = v.status === "realizada";
    const style  = isDone ? VISIT_DONE_STYLE : VISIT_PENDING_STYLE;
    const days   = v.date ? getDaysSince(v.date) : null;

    const title = useTypeLabel
      ? (VISIT_TYPE_MAP[v.visit_type] || v.contact_name || "Visita")
      : (v.contact_name || "Visita");

    return normalizeFeedItem({
      id:       v.id,
      type:     "visit",
      ...style,
      title,
      subtitle: v.date ? formatSmartDate(v.date) : "—",
      age:      days !== null ? `hace ${days}d` : undefined,
      priority: isDone ? FEED_PRIORITY.positive : FEED_PRIORITY.normal,
      _date:    v.date || null,
      onClick:  nav ? () => nav(`/visits/${v.id}`) : undefined,
    });
  });
}

/* ============================= */
/* BUILD EVENT FEED              */
/* ============================= */

/**
 * getPath — (event) => string: route path for each event
 */
export function buildEventFeed(events, { nav, getPath } = {}) {
  return events.map((e) => normalizeFeedItem({
    id:       e.id,
    type:     "event",
    ...EVENT_UPCOMING_STYLE,
    title:    e.title,
    subtitle: formatDateRange(e.date, e.end_date),
    age:      undefined,
    priority: FEED_PRIORITY.normal,
    _date:    e.date || null,
    onClick:  nav && getPath ? () => nav(getPath(e)) : undefined,
  }));
}

/* ============================= */
/* BUILD PRAYER FEED             */
/* ============================= */

export function buildPrayerFeed(prayers, { nav } = {}) {
  return prayers
    .filter((p) => p.status === "activa")
    .map((p) => {
      const isUrgent = p.priority === "alta";
      const style    = isUrgent ? PRAYER_URGENT_STYLE : PRAYER_ACTIVE_STYLE;
      return normalizeFeedItem({
        id:       p.id,
        type:     "prayer",
        ...style,
        title:    p.title || p.description || "Oración",
        subtitle: p.person_name || p.family_name || undefined,
        age:      p.created_at ? `hace ${getDaysSince(p.created_at)}d` : undefined,
        priority: isUrgent ? FEED_PRIORITY.warning : FEED_PRIORITY.normal,
        _date:    p.created_at || null,
        onClick:  nav ? () => nav(`/prayers/${p.id}`) : undefined,
      });
    });
}

/* ============================= */
/* BUILD PRIORITY FEED           */
/* Converts intelligence engine  */
/* priorities → FeedItem[]       */
/* Enables priorities to mix     */
/* naturally into timelines      */
/* ============================= */

/**
 * priorities: Array<{ key, message, level }> from buildOperationalPriorities()
 * actionMap:  Record<key, () => void>
 */
export function buildPriorityFeed(priorities, { actionMap = {} } = {}) {
  return priorities.map((p) => {
    const style = p.level === "critical" ? PRIORITY_CRITICAL_STYLE
                : p.level === "high"     ? PRIORITY_HIGH_STYLE
                :                          PRIORITY_MEDIUM_STYLE;
    const feedPriority = p.level === "critical" ? FEED_PRIORITY.critical
                       : p.level === "high"     ? FEED_PRIORITY.warning
                       :                          FEED_PRIORITY.normal;
    return normalizeFeedItem({
      id:         `priority-${p.key}`,
      type:       "priority",
      ...style,
      title:      p.message,
      priority:   feedPriority,
      _date:      null,
      onClick:    actionMap[p.key],
      meta:       { priorityKey: p.key, priorityLevel: p.level },
    });
  });
}

/* ============================= */
/* GROUP FEED BY CONTEXT         */
/* Semantic temporal grouping    */
/* for MiniTimelineCard groups   */
/* ============================= */

/**
 * Groups a merged feed into semantic sections:
 * - "Crítico"  — FEED_PRIORITY.critical items (undated priorities)
 * - "Próximos" — future-dated items (upcoming events)
 * - "Reciente" — past-dated or undated non-critical items
 *
 * Returns Array<{ label, items }> ready for MiniTimelineCard groups prop.
 */
export function groupFeedByContext(items) {
  const todayStr = new Date().toISOString().split("T")[0];

  const critical = items.filter(
    (i) => (i.priority ?? FEED_PRIORITY.normal) === FEED_PRIORITY.critical
  );
  const upcoming = items.filter(
    (i) =>
      i._date &&
      i._date >= todayStr &&
      (i.priority ?? FEED_PRIORITY.normal) > FEED_PRIORITY.critical
  );
  const recent = items.filter(
    (i) =>
      (i.priority ?? FEED_PRIORITY.normal) > FEED_PRIORITY.critical &&
      (!i._date || i._date < todayStr)
  );

  return [
    critical.length > 0 ? { label: "Crítico",  items: critical } : null,
    upcoming.length > 0 ? { label: "Próximos", items: upcoming } : null,
    recent.length   > 0 ? { label: "Reciente", items: recent   } : null,
  ].filter(Boolean);
}

/* ============================= */
/* MERGE OPERATIONAL FEED        */
/* ============================= */

/**
 * Combines N feed arrays into one sorted, deduplicated feed.
 * Sorts by: 1. priority (critical first), 2. date (most recent first).
 * filter: optional (item) => boolean predicate
 */
export function mergeOperationalFeed(feeds, { limit, filter } = {}) {
  const seen   = new Set();
  const merged = feeds.flat().filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    if (filter && !filter(item)) return false;
    return true;
  });

  merged.sort((a, b) => {
    const pa = a.priority ?? FEED_PRIORITY.normal;
    const pb = b.priority ?? FEED_PRIORITY.normal;
    if (pa !== pb) return pa - pb;
    // Within same priority: undated items first, then most-recent-first
    if (!a._date && !b._date) return 0;
    if (!a._date) return -1;
    if (!b._date) return 1;
    return b._date.localeCompare(a._date);
  });

  return limit ? merged.slice(0, limit) : merged;
}
